import { DateTime } from "luxon";

/** Format of the dates in the IDs of the custom date filters, which are kept in the URL */
const FILTER_ID_DATE_FORMAT = "yyyy-MM-dd";

/** Prefix of the ID of a filter for the days since a date up to today, e.g. "since:2026-10-27" */
const SINCE_DATE_FILTER_ID_PREFIX = "since:";

/** Prefix of the ID of a filter for a date range, e.g. "range:2026-10-01_2026-10-27" */
const DATE_RANGE_FILTER_ID_PREFIX = "range:";

/** Separates the first and the last day in the ID of a date range filter */
const DATE_RANGE_SEPARATOR = "_";

/** The kinds of date filters for which the user picks the dates */

/** The days a date filter matches, from the start of the first to the end of the last */

/** Returns the time of the date an entity is filtered by, or undefined if it has none */

/** Returns the ID of the filter for the days since the date, up to and including today */
export function getSinceDateFilterId(date) {
  return `${SINCE_DATE_FILTER_ID_PREFIX}${toFilterIdDate(date)}`;
}

/** Returns the ID of the filter for the days from the first to the last date, in either order */
export function getDateRangeFilterId(first, last) {
  const [start, end] = first <= last ? [first, last] : [last, first];
  return `${DATE_RANGE_FILTER_ID_PREFIX}${toFilterIdDate(start)}${DATE_RANGE_SEPARATOR}${toFilterIdDate(end)}`;
}

/** Returns the kind of the custom date filter of the ID, or undefined if it is none or invalid */
export function getCustomDateFilterKind(filterId) {
  if (!parseCustomDateFilterId(filterId)) {
    return undefined;
  }
  return filterId.startsWith(SINCE_DATE_FILTER_ID_PREFIX) ? "since" : "range";
}

/** Returns the days the custom date filter of the ID matches, or undefined if it is none or invalid */
export function parseCustomDateFilterId(filterId) {
  if (filterId.startsWith(SINCE_DATE_FILTER_ID_PREFIX)) {
    const day = fromFilterIdDate(filterId.slice(SINCE_DATE_FILTER_ID_PREFIX.length));
    const endOfToday = DateTime.now().endOf("day");
    // A day after today cannot be picked, and would match no day
    if (!day || day > endOfToday) {
      return undefined;
    }
    return {
      start: day.startOf("day"),
      end: endOfToday
    };
  }
  if (filterId.startsWith(DATE_RANGE_FILTER_ID_PREFIX)) {
    const days = filterId.slice(DATE_RANGE_FILTER_ID_PREFIX.length).split(DATE_RANGE_SEPARATOR);
    if (days.length !== 2) {
      return undefined;
    }
    const [first, last] = days.map(fromFilterIdDate);
    if (!first || !last || last < first) {
      return undefined;
    }
    return {
      start: first.startOf("day"),
      end: last.endOf("day")
    };
  }
  return undefined;
}

/** Returns the text that tells which days a custom date filter matches, e.g. "Oct 1 - Oct 27, 2026" */
export function formatDateFilterSpan({
  start,
  end
}) {
  if (start.hasSame(end, "day")) {
    return start.toLocaleString(DateTime.DATE_MED);
  }
  const startFormat = start.hasSame(end, "year") ? {
    month: "short",
    day: "numeric"
  } : DateTime.DATE_MED;
  return `${start.toLocaleString(startFormat)} - ${end.toLocaleString(DateTime.DATE_MED)}`;
}

/**
 * Returns the filter options for the custom date filters among the IDs, which match the entities
 * whose date is on the picked days. The IDs that are no custom date filter are left out.
 */
export function getCustomDateFilterOptions(filterIds, getEntityTime) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const span = parseCustomDateFilterId(filterId);
    if (!span) {
      return [];
    }
    return [[filterId, {
      id: filterId,
      label: getCustomDateFilterKind(filterId) === "since" ? `From ${span.start.toLocaleString(DateTime.DATE_MED)}` : formatDateFilterSpan(span),
      hasEntityPassedFilter: entity => isTimeInSpan(getEntityTime(entity), span)
    }]];
  }));
}

/**
 * Returns the filter options for the recent days: today, yesterday and the last 7, 30 and 365
 * days, the last ones including today.
 */
export function getRecentDateFilterOptions(getEntityTime) {
  const today = DateTime.now();
  const yesterday = today.minus({
    days: 1
  });

  /* eslint-disable @typescript-eslint/no-magic-numbers -- the number of days of each option */
  const spans = {
    today: {
      label: "Today",
      span: {
        start: today.startOf("day"),
        end: today.endOf("day")
      }
    },
    yesterday: {
      label: "Yesterday",
      span: {
        start: yesterday.startOf("day"),
        end: yesterday.endOf("day")
      }
    },
    last7Days: {
      label: "Last 7 days",
      span: getLastDaysSpan(today, 7)
    },
    last30Days: {
      label: "Last 30 days",
      span: getLastDaysSpan(today, 30)
    },
    last365Days: {
      label: "Last 365 days",
      span: getLastDaysSpan(today, 365)
    }
  };
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  return Object.fromEntries(Object.entries(spans).map(([id, {
    label,
    span
  }]) => [id, {
    id,
    label,
    hasEntityPassedFilter: entity => isTimeInSpan(getEntityTime(entity), span)
  }]));
}

/** Returns the span of the given number of days up to and including today */
function getLastDaysSpan(today, days) {
  return {
    start: today.minus({
      days: days - 1
    }).startOf("day"),
    end: today.endOf("day")
  };
}

/** Whether the time is within the span. An entity without a date is in none */
function isTimeInSpan(time, {
  start,
  end
}) {
  return time !== undefined && time >= start.toMillis() && time <= end.toMillis();
}

/** Returns the date as it is written in a filter ID, in the local time zone */
function toFilterIdDate(date) {
  return DateTime.fromJSDate(date).toFormat(FILTER_ID_DATE_FORMAT);
}

/** Returns the date written in a filter ID, or undefined if it is not a valid date */
function fromFilterIdDate(text) {
  const date = DateTime.fromFormat(text, FILTER_ID_DATE_FORMAT);
  return date.isValid ? date : undefined;
}